import { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Shield } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useControlCenter } from "@/context/ControlCenterContext";
import { ccApi } from "./ccApi";
import { Pill } from "./ccUi";
import MemberTab from "./MemberTab";
import LiveTab from "./LiveTab";
import ContentTab from "./ContentTab";
import CasesTab from "./CasesTab";
import AuditTab from "./AuditTab";
import PromoCoinsTab from "./PromoCoinsTab";

/* Part 7 — derive the moderation target from the current URL so the drawer
 * opens "on" whatever the admin is looking at. */
function targetFromLocation(loc) {
  const m = loc.pathname.match(/^\/u\/([^/]+)/);
  if (m) return { type: "user", handle: decodeURIComponent(m[1]), _tab: "member" };
  const s = new URLSearchParams(loc.search).get("s");
  if (loc.pathname.startsWith("/live") && s) return { type: "live", id: s, _tab: "live" };
  const c = loc.pathname.match(/^\/c\/([^/]+)/);
  if (c) return { type: "community", id: c[1], _tab: "live" };
  return null;
}

export default function AdminControlCenter() {
  const cc = useControlCenter();
  const loc = useLocation();
  const { isAdmin, open, setOpen, tab, setTab, target, setTarget, me, setMe } = cc;

  useEffect(() => { if (isAdmin && open && !me) ccApi.me().then(setMe).catch(() => setMe({ can: {} })); }, [isAdmin, open, me, setMe]);
  useEffect(() => { if (open && !target) { const t = targetFromLocation(loc); if (t) { setTarget(t); setTab(t._tab); } } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = useCallback((t) => { setTarget(t); if (t?._tab) setTab(t._tab); }, [setTarget, setTab]);
  const ctxLabel = useMemo(() => {
    if (!target) return null;
    if (target.type === "user") return `@${target.handle || target.id?.slice(0, 8)}`;
    return `${target.type} ${String(target.id || "").slice(0, 8)}`;
  }, [target]);

  if (!isAdmin) return null;
  const tabs = [["member", "Member"], ["live", "Live"], ["content", "Content / Reports"], ["cases", "Cases"], ["audit", "Audit"]];
  if (me?.can?.promo_coins) tabs.push(["owner", "Owner"]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" data-testid="admin-control-center" className="w-full sm:max-w-xl bg-zinc-950 border-l border-white/10 text-white p-0 flex flex-col overflow-hidden">
        <SheetHeader className="px-4 pt-4 pb-3 border-b border-white/10 text-left" style={{ paddingTop: "max(1rem, env(safe-area-inset-top))" }}>
          <SheetTitle className="font-serif text-xl flex items-center gap-2 text-white">
            <Shield size={18} className="text-rose-400" /> Admin Control Center
            {me && <Pill tone={me.role === "owner" ? "amber" : "blue"} testid="cc-role-pill">{me.role.replace("_", " ")}</Pill>}
          </SheetTitle>
          <SheetDescription className="text-zinc-400 text-xs flex flex-wrap items-center gap-2">
            {ctxLabel ? <>Context: <span data-testid="cc-context-label" className="text-white font-mono">{ctxLabel}</span> <button type="button" data-testid="cc-context-clear" onClick={() => setTarget(null)} className="text-zinc-500 hover:text-white">clear</button></> : "No page context — search below."}
            <span className="ml-auto hidden sm:inline text-zinc-600">⌘/Ctrl+Shift+K</span>
          </SheetDescription>
        </SheetHeader>
        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="mx-4 mt-3 bg-white/5 border border-white/10 flex-wrap h-auto justify-start" data-testid="cc-tabs">
            {tabs.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`cc-tab-${v}`} className="text-xs data-[state=active]:bg-white data-[state=active]:text-black">{l}</TabsTrigger>)}
          </TabsList>
          <div className="flex-1 overflow-y-auto px-4 py-3" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
            <TabsContent value="member" className="mt-0"><MemberTab target={target?.type === "user" ? target : null} setTarget={pick} me={me} /></TabsContent>
            <TabsContent value="live" className="mt-0"><LiveTab target={target?.type === "live" ? target : null} setTarget={pick} me={me} /></TabsContent>
            <TabsContent value="content" className="mt-0"><ContentTab target={target} setTarget={pick} me={me} /></TabsContent>
            <TabsContent value="cases" className="mt-0"><CasesTab setTarget={pick} me={me} /></TabsContent>
            <TabsContent value="audit" className="mt-0"><AuditTab /></TabsContent>
            {me?.can?.promo_coins && <TabsContent value="owner" className="mt-0"><PromoCoinsTab /></TabsContent>}
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

export function ShieldButton() {
  const { isAdmin, openCC, open, liveRoomOpen } = useControlCenter();
  // Inside a Live room the in-rail "Moderate this Live" shield is the single control (owner order: no duplicate over the toolbar).
  if (!isAdmin || open || liveRoomOpen) return null;
  return (
    <button type="button" data-testid="cc-shield-button" onClick={() => openCC()} aria-label="Open Admin Control Center" title="Admin Control Center (⌘/Ctrl+Shift+K)"
      className="fixed z-[110] right-3 w-11 h-11 rounded-full bg-zinc-950/90 backdrop-blur border border-rose-500/50 text-rose-300 shadow-lg shadow-rose-900/30 flex items-center justify-center hover:bg-rose-600 hover:text-white transition-colors"
      style={{ bottom: "max(5.5rem, calc(env(safe-area-inset-bottom) + 5rem))" }}>
      <Shield size={18} />
    </button>
  );
}
